import type { StartOrgSyncResponse } from '../../types';
import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

/**
 * Triggers the org sync orchestration pipeline for an existing migration.
 * Queues an orgsync job that runs export → (skip transform) → import in one shot.
 * Calls POST /api/migrations/{id}/start-org-sync
 */
const startOrgSync = async (
  bearerToken: string,
  migrationId: string
): Promise<StartOrgSyncResponse> => {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${migrationId}/start-org-sync`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(bearerToken),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      logger.error('Start org sync API returned error', {
        status: response.status,
        statusText: response.statusText,
        migrationId,
        url
      });
      throw new Error(`Error starting org sync: ${response.status} ${response.statusText}`);
    }

    const data: StartOrgSyncResponse = await response.json();
    logger.info('Org sync queued successfully', {
      migrationId,
      jobType: data.jobType,
      status: data.status
    });
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to start org sync', {
      error: msg,
      migrationId,
      url: `${getEnv().VITE_EDCI_API_URL}/api/migrations/${migrationId}/start-org-sync`
    });
    throw error;
  }
};

export default startOrgSync;
